import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Button, Nav, Tab } from "react-bootstrap";

import { api_without_cancellation } from "../../api/api";
import { getCaseId, getClientId, mediaRoute } from "../../Utils/helper";
import GenericModalComponent from "../common/Modal/Modal";
import { useFooter } from "../common/shared/FooterContext";
import { useDocumentModal } from "../common/CustomModal/CustomModalContext";

function CourtForm({ handleClose, show, instanceId }) {
  const origin = process.env.REACT_APP_BACKEND_URL;

  const [selectedDocs, setSelectedDocs] = useState([]);
  const [selectedDraftDocs, _setSelectedDraftDocs] = useState([]);
  const [_selectAll, setSelectAll] = useState(false);
  const [selectedTab, setSelectedTab] = useState("GDFT");
  const { footerState, setFooterState } = useFooter();
  const [courtForms, setCourtForms] = useState([]);
  const tableBodyRef = useRef(null);
  const containerRef = useRef(null);

  const { showDocumentModal } = useDocumentModal();

  const buildCourtFormUrl = (court_form_id) => {
    const hasMatchingUrl = footerState?.find(
      (item) =>
        item.url.includes("CourtForm") &&
        item.url.includes(`court_form_id=${court_form_id}`) &&
        item.url.includes(`litigation_id=${instanceId}`)
    );

    if (hasMatchingUrl) {
      return hasMatchingUrl.url;
    }

    const newBaseUrl = `/bp-courtform/${getClientId()}/${getCaseId()}`;
    return `${newBaseUrl}/?court_form_id=${court_form_id}&litigation_id=${instanceId}`;
  };

  const openCourtFormReturnDic = async (court_form_id, form_name) => {
    const url = buildCourtFormUrl(court_form_id);
    const hasMatchingUrl = footerState?.find((item) => item.url === url);

    return {
      url,
      windowName: hasMatchingUrl
        ? `_blank_${hasMatchingUrl.for_doc.id}`
        : `${form_name}_${court_form_id}`,
    };
  };

  const openAllCourtForm = async () => {
    try {
      const docList = await Promise.all(
        selectedDocs.map(async (doc) => {
          return openCourtFormReturnDic(doc.id, doc.court_form_name); // Assuming openDocWordAll returns a promise
        })
      );

      // Delay each document download by index * 1000 ms
      docList.forEach((doc, index) => {
        setTimeout(() => {
          const a = document.createElement("a");
          a.href = doc.url;
          a.target = doc.windowName || "_blank";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }, index * 1000); // Delay each download by 1 second
      });
      fetchEditDocs();
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCourtForms = async () => {
    try {
      const response = await api_without_cancellation.get(
        origin +
          `/api/court-form/get-courtforms/?case_id=${getCaseId()}&client_id=${getClientId()}&litigation_act_id=${instanceId}`
      );
      if (response.status === 200) {
        setCourtForms(response.data.results.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCourtForms();
  }, []);

  const handleCheckboxChange_1 = useCallback(
    (doc) => {
      setSelectedDocs((prevDocs) =>
        prevDocs.some((d) => d.id === doc.id)
          ? prevDocs.filter((d) => d.id !== doc.id)
          : [...prevDocs, doc]
      );
    },
    [setSelectedDocs]
  );

  const fetchEditDocs = async () => {
    try {
      const response = await api_without_cancellation.get(`${origin}/api/create-edit-doc/`);

      if (response.status === 200) {
        const data = response.data;

        setFooterState(data);
      }
    } catch (error) {
      console.error("Error fetching case types:", error);
    }
  };

  const handleDocPreview = (doc) => {
    handleClose();
    showDocumentModal("document", mediaRoute(doc.upload), doc);
  };

  const createFakeRows = () => {
    const container = containerRef.current;
    const tbody = tableBodyRef.current;

    if (!container || !tbody) return;

    const containerHeight = container.offsetHeight;
    const headerHeight = 25;
    const rowHeight = 35;
    const availableHeight = containerHeight - headerHeight;
    const rowsNeeded = Math.floor(availableHeight / rowHeight);

    // Clear existing rows
    tbody.innerHTML = "";

    // Add fake rows
    for (let i = 0; i < rowsNeeded; i++) {
      const row = document.createElement("tr");
      row.innerHTML = "<td className='height-35'> </td>";
      tbody.appendChild(row);
    }
  };

  useEffect(() => {
    createFakeRows();
  }, []);

  const bodyContent = useMemo(
    () => (
      <div className="custom-tab m-t-5">
        <Tab.Container defaultActiveKey={"GenerateCourtFormFromTemplate"}>
          <Nav variant="tabs" className="justify-content-around">
            <Nav.Link
              eventKey="GenerateCourtFormFromTemplate"
              onClick={() => {
                setSelectAll(false);
                setSelectedTab("GDFT");
              }}
            >
              Generate Court Form From Template
            </Nav.Link>
            <Nav.Link
              eventKey="CourtFormDrafts"
              onClick={() => {
                setSelectAll(false);
                setSelectedTab("DD");
              }}
            >
              Court Form Drafts
            </Nav.Link>
          </Nav>
          <div className="d-flex flex-column justify-content-between overflow-scroll invisible-scrollbar">
            <Tab.Content
              style={{
                height: "560px",
                minHeight: "560px",
                maxHeight: "560px",
              }}
            >
              <Tab.Pane eventKey="GenerateCourtFormFromTemplate">
                <>
                  <div className="d-flex align-items-center justify-content-center height-21">
                    <p>
                      Select up to three documents to open in new tabs. Grey PDF icon indicates no
                      background PDF has been uploaded.
                    </p>
                  </div>

                  <div className="table-responsive table--no-card rounded-0 border-0">
                    <table className="table table-borderless table-striped table-earning has-height-25 generate-doc-table">
                      <thead>
                        <tr>
                          <th scope="col" className="width-1" />
                          <th />
                          <th>Name</th>
                          <th />
                          <th />
                        </tr>
                      </thead>
                      <tbody id="table-body-cat" className="generate-doc-body">
                        {courtForms?.map((obj, index) => (
                          <tr key={obj?.id}>
                            <td>
                              <div className="justify-content-center">
                                <input
                                  className="template-checkbox cursor-pointer"
                                  type="checkbox"
                                  // disabled={isCheckboxDisabled(obj?.id)}
                                  checked={selectedDocs.some((d) => d.id === obj?.id)}
                                  onChange={() =>
                                    handleCheckboxChange_1({
                                      ...obj,
                                    })
                                  }
                                />
                              </div>
                            </td>
                            <td style={{ width: "32px" }}>{index + 1}</td>
                            <td
                              className="color-white-space-word-wrap"
                              style={{ textAlign: "left" }}
                            >
                              <span
                                style={{ position: "relative", top: "2px" }}
                                className="ic ic-19 ic-court-form"
                              />{" "}
                              <span>
                                {" "}
                                {obj?.court_form_code}: {obj?.court_form_name || ""}
                              </span>
                            </td>
                            <td />
                            <td>
                              {obj?.background_pdf ? (
                                <i
                                  className="ic-generate-document-pdf ic ic-25"
                                  style={{ position: "relative", top: "2px" }}
                                  onClick={() => handleDocPreview(obj?.background_pdf)}
                                />
                              ) : (
                                <i
                                  className="ic-pdf-icon-grey ic ic-25"
                                  style={{ position: "relative", top: "2px" }}
                                />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              </Tab.Pane>
              <Tab.Pane eventKey="CourtFormDrafts">
                <div className="table-responsive table--no-card rounded-0 border-0">
                  <table className="table table-borderless table-striped table-earning has-height-25">
                    <thead>
                      <tr>
                        <th scope="col" className="width-1" />
                        <th />
                        <th className="">Court Form Name</th>
                        <th className="text-center">Last Accessed</th>
                        <th />
                        <th>Assign Document Generation Task</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 13 }).map((_, index) => (
                        <tr key={index}>
                          <td colSpan={6} />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Tab.Pane>
            </Tab.Content>
            <div className="d-flex justify-content-between p-l-5 p-r-5 m-b-5">
              <Button variant="secondary" onClick={handleClose} className="height-25 py-0">
                Close
              </Button>
              <div className="d-flex">
                <Button
                  variant="primary"
                  onClick={() => openAllCourtForm()}
                  className="height-25 py-0"
                >
                  Open Selected Docs in Tabs
                </Button>
              </div>
            </div>
          </div>
        </Tab.Container>
      </div>
    ),

    [handleClose, selectedDocs, selectedDraftDocs, selectedTab, courtForms]
  );

  return (
    <GenericModalComponent
      show={show}
      handleClose={handleClose}
      title={
        <>
          <i className="ic ic-29 ic-court-form m-r-5" /> Generate Court Form
        </>
      }
      bodyContent={bodyContent}
      dialogClassName="modal-dialog-centered genrate-docs-modal"
      titleClassName="mx-auto d-flex align-items-center justify-content-center font-size-24 height-35 font-weight-semibold text-uppercase popup-heading-color font-weight-500"
      headerClassName="text-center p-0 bg-primary popup-heading-color justify-content-center"
      modalBodyClassName="p-0"
    />
  );
}

export default CourtForm;
